/**
 * <FindOrImportClientModal>
 *
 * Iter 9.47.v - Self-import flow per user spec:
 *   "host can search by email or phone, and if im not in the system, host
 *    can sync first/last/email/phone... that will then detect when the
 *    client subscribes."
 *
 * Two paths:
 *   1. Search existing Phygital users → attach by user_id (instant link).
 *   2. No match → manual create (first/last/email/phone) → stub with
 *      pending_link=true that auto-claims on user signup.
 *
 * Both write to db.provider_clients via:
 *   GET  /api/host/clients/lookup?q=...
 *   POST /api/host/events/:event_id/clients/attach
 *   POST /api/host/events/:event_id/clients/import-manual
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, UserPlus, Mail, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => {
  const token = localStorage.getItem("session_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

export default function FindOrImportClientModal({ open, onClose, eventId, onAdded }) {
  const [tab, setTab] = useState("find"); // find | manual
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manual, setManual] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  const runSearch = async () => {
    if (!query.trim() || query.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/host/clients/lookup?q=${encodeURIComponent(query.trim())}`, { headers: headers() });
      const data = await res.json();
      setResults(data.results || []);
    } catch { /* silent */ }
    setSearching(false);
  };

  const attach = async (u) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/host/events/${eventId}/clients/attach`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ user_id: u.user_id }),
      });
      if (res.ok) {
        toast.success(`Added ${u.first_name || u.email || "client"} to your roster`);
        onAdded?.();
        onClose?.();
      } else {
        toast.error("Could not attach");
      }
    } catch { toast.error("Network error"); }
    setSubmitting(false);
  };

  const importManual = async () => {
    if (!manual.email && !manual.phone) {
      toast.error("Email or phone is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/host/events/${eventId}/clients/import-manual`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(manual),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.linked_to_existing_user) {
          toast.success("Matched and linked to an existing Phygital user");
        } else {
          toast.success("Client added - they'll auto-link when they sign up");
        }
        setManual({ first_name: "", last_name: "", email: "", phone: "" });
        onAdded?.();
        onClose?.();
      } else {
        toast.error("Could not import");
      }
    } catch { toast.error("Network error"); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="find-import-client-modal">
        <DialogHeader>
          <DialogTitle>Add a client</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Search existing Phygital users by email or phone, or import a new client. They'll auto-link the moment they sign up.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b -mt-1 mb-2">
          <button
            type="button"
            onClick={() => setTab("find")}
            className={`px-3 py-2 text-xs font-semibold border-b-2 ${tab === "find" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500"}`}
            data-testid="find-tab"
          >
            <Search className="w-3 h-3 inline mr-1" /> Find in Phygital
          </button>
          <button
            type="button"
            onClick={() => setTab("manual")}
            className={`px-3 py-2 text-xs font-semibold border-b-2 ${tab === "manual" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500"}`}
            data-testid="manual-tab"
          >
            <UserPlus className="w-3 h-3 inline mr-1" /> Import manually
          </button>
        </div>

        {tab === "find" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Email, phone, or name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                data-testid="find-client-query"
                autoFocus
              />
              <Button size="sm" onClick={runSearch} disabled={searching || !query.trim()} data-testid="find-client-search-btn">
                {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              </Button>
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {results.length === 0 && !searching && query && (
                <p className="text-xs text-stone-500 text-center py-3">
                  No matches. Try the "Import manually" tab.
                </p>
              )}
              {results.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => attach(u)}
                  disabled={submitting}
                  className="w-full flex items-center gap-3 p-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-left transition"
                  data-testid={`find-client-result-${u.user_id}`}
                >
                  {u.profile_image ? (
                    <img src={u.profile_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-semibold text-stone-600">
                      {(u.first_name?.[0] || u.email?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-stone-900 truncate">
                      {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.display_name || u.email}
                    </p>
                    <p className="text-[10px] text-stone-500 truncate">
                      {u.email}{u.phone ? ` · ${u.phone}` : ""}
                    </p>
                  </div>
                  <span className="text-[10px] text-purple-600 font-semibold">Add</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "manual" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                value={manual.first_name}
                onChange={(e) => setManual((p) => ({ ...p, first_name: e.target.value }))}
                data-testid="manual-first-name"
              />
              <Input
                placeholder="Last name"
                value={manual.last_name}
                onChange={(e) => setManual((p) => ({ ...p, last_name: e.target.value }))}
                data-testid="manual-last-name"
              />
            </div>
            <div className="relative">
              <Mail className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Email"
                value={manual.email}
                onChange={(e) => setManual((p) => ({ ...p, email: e.target.value }))}
                className="pl-8"
                data-testid="manual-email"
              />
            </div>
            <div className="relative">
              <Phone className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Phone"
                value={manual.phone}
                onChange={(e) => setManual((p) => ({ ...p, phone: e.target.value }))}
                className="pl-8"
                data-testid="manual-phone"
              />
            </div>
            <p className="text-[10px] text-stone-400">
              If they're already on Phygital, we'll match them automatically. Otherwise we'll create a stub - it auto-links the moment they sign up with matching email or phone.
            </p>
            <Button
              onClick={importManual}
              disabled={submitting || (!manual.email && !manual.phone)}
              className="w-full bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="manual-import-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <UserPlus className="w-3 h-3 mr-2" />}
              Add client
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
